import { useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * TrackingScripts — fetches admin-configured GA4 / Meta Pixel / Google Search
 * Console IDs once on mount and injects the corresponding tags into
 * <head>. Renders nothing.
 *
 * - GA4: injects gtag.js loader + config snippet for the configured measurement
 *   ID. Also pushes a virtual page_view to dataLayer on every SPA navigation
 *   (handled by the parent App.js mounting; this component only sets up gtag
 *   config — page-view dispatch piggy-backs on existing route changes via the
 *   `gtag` call exposed globally).
 * - Meta Pixel: injects fbq init for the configured pixel ID + a single
 *   PageView event on first load. Subsequent SPA navs aren't pushed by
 *   default — most marketers prefer manual fbq('track', 'PageView') calls
 *   from key conversion points instead, which is the convention.
 * - Google Search Console: injects a single <meta> verification tag.
 *
 * All three are entirely opt-in: if the admin hasn't configured a value the
 * corresponding tag is not injected at all. No third-party network call
 * happens unless the admin asks for it.
 *
 * Re-injection: once mounted, we don't re-fetch or re-inject (a real reload
 * is simpler and avoids the "loader injected twice" class of bugs).
 */
const TrackingScripts = () => {
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/tracking-settings`)
      .then((res) => {
        if (cancelled) return;
        const cfg = res.data || {};
        injectGA4(cfg.tracking_ga4_id);
        injectMetaPixel(cfg.tracking_meta_pixel_id);
        injectGSC(cfg.tracking_gsc_verification);
      })
      .catch(() => { /* never break the app on a tracking-config fetch */ });
    return () => { cancelled = true; };
  }, []);

  return null;
};

const injectGA4 = (id) => {
  if (!id || typeof id !== 'string') return;
  if (!/^G-[A-Z0-9]+$/i.test(id)) return; // sanity-check format
  if (document.getElementById('ga4-loader')) return; // already injected

  const loader = document.createElement('script');
  loader.id = 'ga4-loader';
  loader.async = true;
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(loader);

  const inline = document.createElement('script');
  inline.id = 'ga4-config';
  inline.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${id.replace(/'/g, '')}');
  `;
  document.head.appendChild(inline);
};

const injectMetaPixel = (id) => {
  if (!id || typeof id !== 'string') return;
  if (!/^\d{6,20}$/.test(id)) return;
  if (document.getElementById('meta-pixel-init')) return;

  const inline = document.createElement('script');
  inline.id = 'meta-pixel-init';
  inline.text = `
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){
    n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${id.replace(/'/g, '')}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(inline);
};

const injectGSC = (content) => {
  if (!content || typeof content !== 'string') return;
  if (document.getElementById('gsc-verification')) return;
  // Some admins paste the full meta tag; extract content="..." if so.
  let value = content.trim();
  const match = value.match(/content="([^"]+)"/i);
  if (match) value = match[1];
  const meta = document.createElement('meta');
  meta.id = 'gsc-verification';
  meta.name = 'google-site-verification';
  meta.content = value;
  document.head.appendChild(meta);
};

export default TrackingScripts;
